import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import beOneLogo from "../../assets/b1-logo-icon.svg";
import { getThemeFeedback } from "../data/feedbackData";
import { useLocale, useT } from "../i18n";
import { ProgressiveRadarChart } from "./ProgressiveRadarChart";

interface EmailCaptureProps {
  onSaveAndContinue: (email: string) => void;
  onConfirmCompanyAndContinue: (companyName: string) => void;
  onPauseForLater: (email: string) => void;
  onGoBack: () => void;
  score: number;
  total: number;
  themeName: string;
  answers: Record<number, string>;
  initialEmail?: string | null;
  companySuggestion: string | null;
  companyConfirmationRequired: boolean;
}

export function EmailCapture({
  onSaveAndContinue,
  onConfirmCompanyAndContinue,
  onPauseForLater,
  onGoBack,
  score,
  total,
  themeName,
  answers,
  initialEmail,
  companySuggestion,
  companyConfirmationRequired,
}: EmailCaptureProps) {
  const t = useT();
  const locale = useLocale();
  const [email, setEmail] = useState(initialEmail ?? "");
  const [companyName, setCompanyName] = useState(companySuggestion ?? "");
  const [error, setError] = useState("");
  const emailInputRef = useRef<HTMLInputElement>(null);

  const focusEmailInput = () => {
    emailInputRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    emailInputRef.current?.focus();
  };

  useEffect(() => {
    setCompanyName(companySuggestion ?? "");
  }, [companySuggestion]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSaveAndContinue = () => {
    if (!email.trim()) {
      setError(t("emailCapture.errorEmpty"));
      focusEmailInput();
      return;
    }
    if (!validateEmail(email)) {
      setError(t("emailCapture.errorInvalid"));
      return;
    }
    setError("");
    onSaveAndContinue(email);
  };

  const handlePauseForLater = () => {
    if (!email.trim()) {
      setError(t("emailCapture.errorEmailForLink"));
      return;
    }
    if (!validateEmail(email)) {
      setError(t("emailCapture.errorInvalid"));
      return;
    }
    setError("");
    onPauseForLater(email);
  };

  const handleConfirmCompany = () => {
    if (!companyName.trim()) {
      setError(t("emailCapture.errorCompany"));
      return;
    }
    setError("");
    onConfirmCompanyAndContinue(companyName.trim());
  };

  const feedback = getThemeFeedback(1, answers, locale);

  // Normalize score to 0-4 scale for radar chart
  const normalizedScore = (score / total) * 4;

  return (
    <div
      className="min-h-screen p-6"
      style={{
        background: "linear-gradient(to bottom right, #02324A, #00AFFD)",
      }}
    >
      <div className="max-w-3xl mx-auto px-0 pt-0 pb-[32px]">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img
            src={beOneLogo}
            alt="Be-one"
            width={96}
            height={96}
            className="h-24 mx-auto mb-4"
          />
          <h2 className="font-[Roboto_Slab] text-white text-2xl mb-2">
            {t("emailCapture.themeTitle")}
            <br />
            {themeName}
          </h2>
        </div>

        {/* Top Continue Button */}
        <button
          type="button"
          onClick={
            companyConfirmationRequired
              ? handleConfirmCompany
              : handleSaveAndContinue
          }
          className="w-full text-white rounded-lg flex items-center justify-center gap-2 transition-colors font-[Roboto] text-lg font-medium px-[24px] py-[12px] mb-8"
          style={{ backgroundColor: "#FF00D2" }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#D900B0")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#FF00D2")
          }
        >
          {t("emailCapture.continue")}
          <ArrowRight className="w-5 h-5" />
        </button>

        {/* Theme Feedback */}
        <div className="text-center rounded-lg bg-white/10 backdrop-blur-sm p-[24px] mb-8">
          <p className="font-[Roboto] leading-relaxed text-white">{feedback}</p>
        </div>

        {/* Radar Chart */}
        <div className="mb-8">
          <ProgressiveRadarChart
            completedThemes={1}
            scores={{
              strategie: normalizedScore,
              marque: 0,
              contenu: 0,
              ia: 0,
            }}
          />
        </div>

        <p className="text-white font-[Roboto] mb-6">
          {t("emailCapture.emailLabel")}
        </p>

        <div className="mb-4">
          <input
            ref={emailInputRef}
            type="email"
            className={`w-full px-4 py-3 border-2 rounded-lg font-[Roboto] focus:outline-none transition-colors bg-white ${
              error
                ? "border-red-500 focus:border-red-500"
                : "border-gray-300 focus:border-[#FF00D2]"
            }`}
            placeholder={t("emailCapture.emailPlaceholder")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSaveAndContinue();
              }
            }}
          />
          {error && (
            <p className="text-red-500 text-sm mt-2 font-[Roboto]">{error}</p>
          )}
        </div>

        <p className="text-white/70 text-sm font-[Roboto] mb-6">
          {t("emailCapture.emailPrivacy")}
        </p>

        {companyConfirmationRequired && (
          <div className="mb-6 rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm p-5">
            <h4 className="mb-2 font-[Roboto_Slab] text-lg text-white">
              {t("emailCapture.company.title")}
            </h4>
            <p className="mb-3 font-[Roboto] text-white/90">
              {companySuggestion
                ? t("emailCapture.company.withSuggestion")
                : t("emailCapture.company.noSuggestion")}
            </p>
            <input
              type="text"
              className={`w-full px-4 py-3 border-2 rounded-lg font-[Roboto] focus:outline-none transition-colors bg-white ${
                error
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-300 focus:border-[#FF00D2]"
              }`}
              placeholder={t("emailCapture.company.placeholder")}
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleConfirmCompany();
                }
              }}
            />
          </div>
        )}

        <div className="space-y-3">
          {companyConfirmationRequired ? (
            <button
              type="button"
              onClick={handleConfirmCompany}
              className="w-full text-white py-4 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors font-[Roboto] text-lg font-medium"
              style={{ backgroundColor: "#FF00D2" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#D900B0")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#FF00D2")
              }
            >
              {t("emailCapture.continue")}
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSaveAndContinue}
              className="w-full text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors font-[Roboto] text-lg font-medium"
              style={{ backgroundColor: "#FF00D2" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#D900B0")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#FF00D2")
              }
            >
              {t("emailCapture.continue")}
              <ArrowRight className="w-5 h-5" />
            </button>
          )}

          <div className="pt-2 border-t border-white/20">
            <p className="text-white/60 text-sm font-[Roboto] mb-3 text-center">
              {t("emailCapture.pauseLater.hint")}
            </p>
            <button
              type="button"
              onClick={handlePauseForLater}
              className="w-full text-white/70 hover:text-white py-2 px-6 flex items-center justify-center gap-2 transition-colors font-[Roboto] text-sm"
              style={{ background: "transparent" }}
            >
              {t("emailCapture.pauseLater.button")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={onGoBack}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors font-[Roboto] text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("emailCapture.previous")}
          </button>
        </div>
      </div>
    </div>
  );
}
